export type ProjectLink = {
    label: string
    url: string
}

export type ProjectMetric = {
    label: string
    value: string
}

export type ProjectHighlight = {
    label: string
    detail: string
}

export type ProjectSection = {
    id: string
    title: string
    intro: string
    highlights: ProjectHighlight[]
    mermaid?: string
}

export type ProjectDetail = {
    id: string
    name: string
    category: string
    role: string
    status: string
    duration: string
    summary: string
    readingTime: string
    heroNote: string
    keyMetrics: ProjectMetric[]
    tags: string[]
    stack: string[]
    links: ProjectLink[]
    sections: ProjectSection[]
    relatedLinks: ProjectLink[]
}

export type CategoryCard = {
    id: string
    projectId: string
    title: string
    description: string
    category: string
    status: string
    role: string
    duration: string
    keyMetrics: string[]
    tags: string[]
    stack: string[]
    detailSection?: string
    siteUrl?: string
    sourceUrl?: string
}

export type CategoryPage = {
    eyebrow: string
    title: string
    intro: string
    purpose: string
    cards: CategoryCard[]
}

const clientBaseStack = ['Next.js', 'React', 'Tailwind CSS', 'SEO', 'Admin workflows']

function caseStudyHref(projectId: string, sectionId = 'executive-summary') {
    return `/projects/${projectId}#${sectionId}`
}

function createClientProject(detail: {
    id: string
    name: string
    url: string
    audience: string
    specialty: string
    outcome: string
}) {
    return {
        id: detail.id,
        name: detail.name,
        category: 'Client Project',
        role: 'Full-Stack Developer',
        status: 'production',
        duration: 'Client delivery',
        summary: `${detail.name} is one of the live journal properties delivered within the FDRP publishing ecosystem, built to serve ${detail.audience} with a performant, searchable, mobile-friendly journal experience.`,
        readingTime: '6 min read',
        heroNote: `This case study focuses on client requirements, delivery quality, SEO, performance, and maintainability for ${detail.name}.`,
        keyMetrics: [
            { label: 'Delivery type', value: 'Production journal website' },
            { label: 'Priority', value: 'SEO + discoverability' },
            { label: 'Outcome', value: detail.outcome },
        ],
        tags: ['Client work', 'Publishing', 'SEO', 'Responsive design'],
        stack: clientBaseStack,
        links: [
            { label: 'Live website', url: detail.url },
            { label: 'View related platform case study', url: caseStudyHref('fdrp-platform', 'client-projects') },
        ],
        sections: [
            {
                id: 'executive-summary',
                title: 'Executive Summary',
                intro: `${detail.name} was built as part of a broader journal-platform delivery where each property needed its own brand presence while retaining the operational quality of the shared publishing stack.`,
                highlights: [
                    { label: 'Audience', detail: detail.audience },
                    { label: 'Specialty', detail: detail.specialty },
                    { label: 'Primary outcome', detail: detail.outcome },
                ],
            },
            {
                id: 'client-requirements',
                title: 'Client Requirements',
                intro: 'Each journal site had to balance brand identity with consistency, publishing clarity, and operational efficiency for repeat editorial workflows.',
                highlights: [
                    { label: 'Visibility', detail: 'Make the journal discoverable through strong information hierarchy and search-friendly content structure.' },
                    { label: 'Trust', detail: 'Present clear publication information, policies, and submission pathways that support academic credibility.' },
                    { label: 'Operations', detail: 'Keep the site maintainable inside a larger publishing ecosystem instead of treating it as a one-off build.' },
                ],
            },
            {
                id: 'delivery',
                title: 'Features Delivered',
                intro: 'The implementation focused on the publication flows and content surfaces that matter most to authors, readers, and editorial teams.',
                highlights: [
                    { label: 'Public surfaces', detail: 'Journal home, overview, archives, policy pages, and submission-related entry points.' },
                    { label: 'Responsive design', detail: 'Layouts tuned for desktop and mobile so publishing information remains readable on smaller screens.' },
                    { label: 'Admin alignment', detail: 'Shared structure with the ecosystem so internal teams can manage content and workflows predictably.' },
                ],
            },
            {
                id: 'performance',
                title: 'Performance, SEO, and Security',
                intro: 'Client value came from more than visual delivery; the sites also needed to perform well and remain search-friendly in a content-heavy environment.',
                highlights: [
                    { label: 'SEO', detail: 'Structured layouts, clean content presentation, and discoverability-focused page composition.' },
                    { label: 'Performance', detail: 'Render-path discipline, modular page construction, and reduced request friction in public-facing routes.' },
                    { label: 'Security', detail: 'Conservative content handling, role-aware operational boundaries, and safer publishing-system integration points.' },
                ],
            },
            {
                id: 'business-impact',
                title: 'Business Impact',
                intro: 'The project helped convert publishing requirements into a usable digital product that supports both discoverability and ongoing journal operations.',
                highlights: [
                    { label: 'Business value', detail: 'Improved digital credibility for the journal while keeping the site operationally aligned with the larger platform.' },
                    { label: 'Maintenance', detail: 'Shared design and implementation patterns reduce long-term overhead across the portfolio of sites.' },
                    { label: 'Outcome', detail: detail.outcome },
                ],
            },
        ],
        relatedLinks: [
            { label: 'Back to My Works', url: '/myworks' },
            { label: 'Back to Case Studies', url: '/case-studies' },
        ],
    } satisfies ProjectDetail
}

export const PROJECT_DETAILS: Record<string, ProjectDetail> = {
    'medical-ai-bot': {
        id: 'medical-ai-bot',
        name: 'Medical AI Bot',
        category: 'AI Product / Healthcare',
        role: 'Full-Stack Engineer',
        status: 'Proof of concept',
        duration: 'Prototype',
        summary: 'A healthcare-oriented assistant concept designed to turn symptom context into triage-style guidance through a structured AI workflow rather than an unbounded chatbot experience.',
        readingTime: '12 min read',
        heroNote: 'This project is presented as an engineering case study around decision support, AI workflow design, responsible product boundaries, and healthcare-adjacent user experience.',
        keyMetrics: [
            { label: 'Domain', value: 'Healthcare support' },
            { label: 'System type', value: 'Symptom-to-guidance workflow' },
            { label: 'Portfolio value', value: 'Applied AI beyond generic chat' },
        ],
        tags: ['AI', 'Healthcare', 'NLP', 'Decision support'],
        stack: ['Python', 'Flask', 'NLP', 'ML models', 'Frontend UI'],
        links: [],
        sections: [
            {
                id: 'hero',
                title: 'Hero Context',
                intro: 'The Medical AI Bot was not built to replace clinical care. It was framed as a structured assistant that helps users move from vague symptom input toward clearer next-step guidance.',
                highlights: [
                    { label: 'What was built', detail: 'A symptom-aware assistant concept that organizes triage-style responses through a guided AI flow.' },
                    { label: 'Why it matters', detail: 'Healthcare-related inputs are high-friction and high-anxiety. A useful product needs clarity, caution, and structure.' },
                    { label: 'Design principle', detail: 'Constrain the system around support, routing, and interpretation instead of pretending to be a doctor.' },
                ],
            },
            {
                id: 'executive-summary',
                title: 'Executive Summary',
                intro: 'The core problem was not just model accuracy. It was product framing. Users need an interaction model that can gather symptoms, classify intent, and surface next-step guidance in a way that feels organized and responsible.',
                highlights: [
                    { label: 'Problem statement', detail: 'Unstructured symptom searches are confusing and often return either overwhelming or unreliable answers.' },
                    { label: 'Product goal', detail: 'Create a guided assistant that narrows uncertainty and supports triage-style reasoning through structured prompts and model outputs.' },
                    { label: 'Business context', detail: 'This project demonstrates how AI can be packaged into a domain-specific workflow rather than a generic prompt box.' },
                ],
            },
            {
                id: 'market-problem',
                title: 'Market Problem and Existing Solutions',
                intro: 'Most consumer health-search experiences sit at one of two extremes: broad search results with little personalization, or black-box symptom checkers that do not explain enough of their reasoning.',
                highlights: [
                    { label: 'Existing gap', detail: 'Users want faster interpretation of symptoms without needing to understand medical terminology or parse dense pages.' },
                    { label: 'Observed weakness', detail: 'Generic chat systems can sound confident without enforcing safe scope or structured questioning.' },
                    { label: 'Why this project was built', detail: 'To explore a middle ground: a guided interface that narrows context, applies classification logic, and returns support-oriented outputs.' },
                ],
            },
            {
                id: 'product-vision',
                title: 'Product Vision and User Flow',
                intro: 'The vision was a telemedicine-adjacent assistant that behaves more like a triage funnel than a free-form chatbot.',
                highlights: [
                    { label: 'User persona', detail: 'A user with a symptom concern who wants quick guidance before deciding whether to seek urgent care, book a consultation, or monitor symptoms.' },
                    { label: 'User journey', detail: 'Input symptoms -> clarify severity or duration -> classify probable context -> surface next-step guidance and caution notes.' },
                    { label: 'Feature planning', detail: 'Prioritize clarity, symptom routing, and explainable outputs before adding richer account or tracking features.' },
                ],
            },
            {
                id: 'system-design',
                title: 'System Architecture',
                intro: 'The architecture needed to support structured intake, model-assisted interpretation, and response rendering while keeping the scope narrow enough to remain understandable.',
                highlights: [
                    { label: 'Frontend role', detail: 'Collect symptom context and present clarification prompts and guidance in a controlled, readable flow.' },
                    { label: 'Backend role', detail: 'Flask services handle request routing, symptom normalization, and coordination with the inference layer.' },
                    { label: 'AI pipeline', detail: 'Symptom text is normalized, mapped into structured features or intents, then passed through classification or recommendation logic before response generation.' },
                ],
                mermaid: `flowchart TD
    A[User symptom input] --> B[Clarification prompts]
    B --> C[Flask API]
    C --> D[Symptom normalization]
    D --> E[ML or NLP inference]
    E --> F[Guidance composer]
    F --> G[Risk-aware response UI]`,
            },
            {
                id: 'ai-pipeline',
                title: 'AI Pipeline and Prompting',
                intro: 'The AI value in this project comes from controlled interpretation rather than open-ended conversation.',
                highlights: [
                    { label: 'Prompt engineering', detail: 'Responses should request missing context, avoid overclaiming certainty, and keep next-step guidance concise.' },
                    { label: 'Model selection', detail: 'A practical combination of NLP classification and deterministic workflow logic is safer than fully delegating triage behavior to one generative model.' },
                    { label: 'Reasoning flow', detail: 'Classify symptoms, severity indicators, and possible pathways first; generate human-readable guidance second.' },
                ],
            },
            {
                id: 'data-api',
                title: 'Data, API, and Security Thinking',
                intro: 'Even as a prototype, healthcare-adjacent products require disciplined boundaries around data flow and safety assumptions.',
                highlights: [
                    { label: 'Database design', detail: 'A production-ready version would need separated records for session state, symptom history, user preferences, and escalation outcomes.' },
                    { label: 'API flow', detail: 'Client submits symptom state -> backend validates payload -> inference path runs -> response object returns guidance, severity cues, and disclaimers.' },
                    { label: 'Security considerations', detail: 'Authentication, consent, auditability, and protected storage become mandatory as soon as user identity or health data enters the system.' },
                ],
            },
            {
                id: 'engineering-challenges',
                title: 'Challenges, Mistakes, and Tradeoffs',
                intro: 'The difficult part is not making the assistant talk. It is designing something that stays useful without drifting into unsafe ambiguity.',
                highlights: [
                    { label: 'Technical challenge', detail: 'Symptom language is vague, inconsistent, and emotionally loaded, which makes clean classification difficult.' },
                    { label: 'Mistake avoided', detail: 'Treating the product like a broad medical chatbot would have increased risk and reduced explainability.' },
                    { label: 'Tradeoff', detail: 'A narrower, guided workflow reduces conversational freedom but produces more reliable and supportable outputs.' },
                ],
            },
            {
                id: 'impact',
                title: 'Impact, Lessons, and Future Improvements',
                intro: 'The strongest outcome of this project is the product framing: it demonstrates how to package AI into a structured domain workflow with caution and intent.',
                highlights: [
                    { label: 'Business impact', detail: 'Shows how AI can support healthcare navigation through focused workflows rather than generic assistant behavior.' },
                    { label: 'Lessons learned', detail: 'Scope control, response structure, and product boundaries matter as much as the model itself.' },
                    { label: 'Future improvements', detail: 'Escalation scoring, appointment integration, multilingual symptom handling, and safer audit-ready response templates.' },
                ],
            },
        ],
        relatedLinks: [
            { label: 'Back to My Works', url: '/myworks' },
            { label: 'View System Design section', url: '/system-architecture-design' },
        ],
    },
    'web-explainer': {
        id: 'web-explainer',
        name: 'Web Explainer',
        category: 'AI Product / LLM Workflow',
        role: 'Full-Stack / AI Integration Engineer',
        status: ' prototype',
        duration: 'Personal product',
        summary: 'A webpage-understanding assistant that fetches long-form web content, cleans it, routes it through an LLM pipeline, and streams explanations back to the interface.',
        readingTime: '11 min read',
        heroNote: 'This case study focuses on retrieval flow, parsing architecture, prompt design, streamed UX, and how to turn raw pages into a readable AI product.',
        keyMetrics: [
            { label: 'Core value', value: 'Fast webpage understanding' },
            { label: 'Interaction model', value: 'Fetch -> parse -> explain' },
            { label: 'Verified source', value: 'Public GitHub repository' },
        ],
        tags: ['LLM', 'Parsing', 'Streaming UX', 'Summarization'],
        stack: ['HTML', 'JavaScript', 'Web content parsing', 'LLM integration'],
        links: [
            { label: 'Live demo', url: 'https://web-explainer.vercel.app/' },
            { label: 'Source code', url: 'https://github.com/tuhinkairi/Web-Explainer'}
        ],
        sections: [
            {
                id: 'hero',
                title: 'Hero Context',
                intro: 'Web Explainer was built around a common product pain point: users often need the main idea of a webpage, not every detail on the page.',
                highlights: [
                    { label: 'Product vision', detail: 'Let a user pass a webpage and receive a quick, readable explanation instead of skimming manually.' },
                    { label: 'User workflow', detail: 'Paste a URL -> fetch and clean content -> run explanation pipeline -> stream response back.' },
                    { label: 'Why it matters', detail: 'The product value comes from orchestration quality, not only raw model output.' },
                ],
            },
            {
                id: 'executive-summary',
                title: 'Executive Summary',
                intro: 'This project is a compact example of turning LLM capability into product utility by solving the retrieval, parsing, and delivery layers around the model.',
                highlights: [
                    { label: 'Problem statement', detail: 'Dense webpages take too long to read when users only want the core explanation or summary.' },
                    { label: 'Solution approach', detail: 'Combine content extraction, prompt shaping, and streamed rendering into a single user-facing workflow.' },
                    { label: 'Business context', detail: 'Useful for productivity, education, and research-support workflows where time-to-understanding matters.' },
                ],
            },
            {
                id: 'workflow',
                title: 'User Workflow and Information Architecture',
                intro: 'The product had to separate user intent from content acquisition. That means deciding whether the user is asking a direct question or submitting a page to be interpreted.',
                highlights: [
                    { label: 'Input routing', detail: 'Differentiate URL-based explanation requests from regular prompt interactions.' },
                    { label: 'Information architecture', detail: 'Keep the input step simple, show work-in-progress, and present the explanation in scannable chunks.' },
                    { label: 'Frontend behavior', detail: 'Streaming output makes the system feel active rather than stalled behind model latency.' },
                ],
            },
            {
                id: 'system-design',
                title: 'Browser and Parsing Architecture',
                intro: 'The engineering challenge is upstream of the model: raw HTML is noisy, repetitive, and often unhelpful without preprocessing.',
                highlights: [
                    { label: 'Fetch layer', detail: 'Retrieve webpage content from the submitted URL.' },
                    { label: 'Parsing layer', detail: 'Strip noise, isolate meaningful text, and structure the prompt input so the model sees useful context.' },
                    { label: 'Output layer', detail: 'Return a structured explanation progressively instead of waiting for a monolithic final answer.' },
                ],
                mermaid: `flowchart LR
    A[URL input] --> B[Fetch webpage]
    B --> C[Parse and clean content]
    C --> D[Prompt builder]
    D --> E[LLM reasoning]
    E --> F[Streaming explanation UI]`,
            },
            {
                id: 'llm-pipeline',
                title: 'LLM Pipeline and Reasoning Flow',
                intro: 'The strongest technical idea in this project is that prompt quality depends heavily on preprocessing quality.',
                highlights: [
                    { label: 'Prompt pipeline', detail: 'Content is cleaned and framed before model invocation so the prompt focuses on explanation, not raw extraction.' },
                    { label: 'Reasoning flow', detail: 'Summarize the page, identify the main idea, and express the result in a more human-readable structure.' },
                    { label: 'Retrieval strategy', detail: 'Keep the context compact and relevant rather than passing the entire page blindly.' },
                ],
            },
            {
                id: 'backend-architecture',
                title: 'Backend Architecture and Scaling Strategy',
                intro: 'Even a lightweight AI tool benefits from thinking ahead about latency, repeated requests, and future workload growth.',
                highlights: [
                    { label: 'Backend role', detail: 'Coordinate fetch, parsing, prompt assembly, and model response handling.' },
                    { label: 'Scaling strategy', detail: 'Add caching for repeated URLs, chunk large pages, and separate fetch/parsing work from generation work when usage grows.' },
                    { label: 'Optimization direction', detail: 'Stream partial output early to improve perceived performance and trust.' },
                ],
            },
            {
                id: 'engineering-challenges',
                title: 'Engineering Challenges and Decisions',
                intro: 'The hard problems here are almost all about input quality and UX pacing.',
                highlights: [
                    { label: 'Challenge', detail: 'Noisy HTML reduces model clarity and can push the answer off-topic.' },
                    { label: 'Decision', detail: 'Handle cleanup and structure before inference rather than trying to solve everything inside the prompt.' },
                    { label: 'Tradeoff', detail: 'More preprocessing adds complexity, but it dramatically improves the usability of the final explanation.' },
                ],
            },
            {
                id: 'roadmap',
                title: 'Future Roadmap',
                intro: 'The next step is not just a smarter answer. It is a broader explanation platform.',
                highlights: [
                    { label: 'Future features', detail: 'Saved explanations, multi-page comparison, shareable summaries, and source-linked explanation blocks.' },
                    { label: 'AI evolution', detail: 'Topic-aware prompts, configurable explanation depth, and agentic follow-up questions.' },
                    { label: 'Business impact', detail: 'Positions AI as a practical reading accelerator for research, learning, and product discovery.' },
                ],
            },
        ],
        relatedLinks: [
            { label: 'Back to My Works', url: '/myworks' },
            { label: 'Back to Case Studies', url: '/case-studies' },
        ],
    },
    'e-store': {
        id: 'e-store',
        name: 'E-store',
        category: 'Commerce Project',
        role: 'Frontend / Full-Stack Developer',
        status: 'Backend',
        duration: 'Personal build',
        summary: 'A storefront-oriented project that expands the portfolio into commerce UX, product discovery, and reusable catalog patterns.',
        readingTime: '8 min read',
        heroNote: 'This case study focuses on storefront architecture, product presentation flows, reusable UI structure, and how commerce work differs from AI or publishing projects.',
        keyMetrics: [
            { label: 'Portfolio role', value: 'Commerce breadth' },
            { label: 'Verified source', value: 'Public GitHub repository' },
            { label: 'Primary value', value: 'Catalog and storefront UX' },
        ],
        tags: ['E-commerce', 'Catalog', 'UI architecture'],
        stack: ['Web UI', 'Product listing patterns', 'Reusable components'],
        links: [
            { label: 'Source code', url: 'https://github.com/tuhinkairi/E-store' },
        ],
        sections: [
            {
                id: 'executive-summary',
                title: 'Executive Summary',
                intro: 'E-store adds a commerce-shaped problem to the portfolio: how to make product browsing feel clear, structured, and scalable.',
                highlights: [
                    { label: 'Project overview', detail: 'A storefront build centered on listing, browsing, and product-oriented interface patterns.' },
                    { label: 'Business value', detail: 'Shows range beyond AI workflows and journal platforms.' },
                    { label: 'Why it matters', detail: 'Commerce UX has different priorities: product clarity, browsing momentum, and repeatable structure.' },
                ],
            },
            {
                id: 'system-design',
                title: 'Architecture, State, and API Thinking',
                intro: 'The key architectural value is creating a product-grid and detail system that can later grow into richer commerce behavior.',
                highlights: [
                    { label: 'Architecture', detail: 'Reusable card/list/detail patterns for product discovery and expansion.' },
                    { label: 'State management', detail: 'A natural next step would be shared state for catalog filters, cart behavior, and session persistence.' },
                    { label: 'API design', detail: 'A production variant would separate catalog retrieval, user state, inventory, and checkout concerns into distinct endpoints.' },
                ],
            },
            {
                id: 'implementation',
                title: 'Features, Challenges, and Future Scope',
                intro: 'The value of this project is not only what it currently does, but how it could scale into fuller commerce flows.',
                highlights: [
                    { label: 'Features', detail: 'Catalog presentation, storefront browsing, and reusable product-focused UI structure.' },
                    { label: 'Challenges', detail: 'Balancing visual clarity with enough component reuse to support future scale.' },
                    { label: 'Future scope', detail: 'Authentication, cart flows, search, filtering, inventory, and payment-oriented extensions.' },
                ],
            },
        ],
        relatedLinks: [
            { label: 'Back to My Works', url: '/myworks' },
        ],
    },
    'onepointprep': {
        id: 'onepointprep',
        name: 'OnePointPrep',
        category: 'AI Product / Education',
        role: 'Full-Stack / AI Product Builder',
        status: 'Backend',
        duration: 'Personal build',
        summary: 'An AI-assisted study-preparation concept aimed at students who need focused help rather than broad, unstructured educational content.',
        readingTime: '10 min read',
        heroNote: 'This case study focuses on learning flow, information design, scalability for student-facing products, and how AI can support concise prep journeys.',
        keyMetrics: [
            { label: 'Audience', value: 'Students preparing for exams' },
            { label: 'System type', value: 'AI-assisted learning workflow' },
            { label: 'Verified source', value: 'Public GitHub repository' },
        ],
        tags: ['Education', 'AI', 'Learning flow', 'Student product'],
        stack: ['AI workflow', 'Learning UX', 'Structured prep flow'],
        links: [
            { label: 'Source code', url: 'https://github.com/tuhinkairi/OnePointPrep' },
        ],
        sections: [
            {
                id: 'executive-summary',
                title: 'Executive Summary',
                intro: 'OnePointPrep shifts the AI portfolio from interview simulation toward student revision, which introduces a different pace, mindset, and product need.',
                highlights: [
                    { label: 'Problem statement', detail: 'Students often need fast, targeted revision help rather than long-form educational content.' },
                    { label: 'Product vision', detail: 'Create an AI study assistant that supports focused preparation and shorter learning loops.' },
                    { label: 'Business impact', detail: 'Broadens the portfolio with an education-facing AI product category.' },
                ],
            },
            {
                id: 'user-journey',
                title: 'User Journey and Learning Flow',
                intro: 'The educational challenge is not only generating content. It is helping the learner move through a revision flow with low friction.',
                highlights: [
                    { label: 'User journey', detail: 'Select topic -> request support -> receive concise study guidance -> continue revision loop.' },
                    { label: 'Learning flow', detail: 'The product should prioritize targeted help, not overwhelm the learner with too much content at once.' },
                    { label: 'Search strategy', detail: 'A mature version could support topic indexing, concept lookup, and curriculum-aware query routing.' },
                ],
            },
            {
                id: 'system-design',
                title: 'Architecture and System Design',
                intro: 'The most valuable architectural pattern here is content orchestration: matching learners with the right depth and structure at the right time.',
                highlights: [
                    { label: 'Architecture', detail: 'A student-facing web interface backed by AI-assisted content generation or retrieval.' },
                    { label: 'Database design', detail: 'A fuller system would track users, study history, saved topics, progress signals, and revision preferences.' },
                    { label: 'API design', detail: 'Topic request endpoints, content-response endpoints, and progress or analytics endpoints would form the product backbone.' },
                ],
            },
            {
                id: 'scalability',
                title: 'Performance, Scalability, and Engineering Decisions',
                intro: 'As usage grows, the main challenge becomes responsiveness under repeated, topic-driven requests.',
                highlights: [
                    { label: 'Performance', detail: 'Caching repeated topic lookups and standardizing response formats can improve perceived speed.' },
                    { label: 'Scalability', detail: 'Topic segmentation, prompt templating, and saved learning state make the system easier to extend across subjects.' },
                    { label: 'Engineering decisions', detail: 'Keep the workflow concise and user-goal oriented rather than building a generic AI chat shell.' },
                ],
            },
            {
                id: 'future-roadmap',
                title: 'Challenges, Impact, and Future Roadmap',
                intro: 'The product opportunity grows when the experience becomes more structured, measurable, and personalized.',
                highlights: [
                    { label: 'Challenges', detail: 'Balancing helpfulness with brevity so the system supports revision instead of creating new overload.' },
                    { label: 'Impact', detail: 'Demonstrates education product thinking and AI orchestration around study behavior.' },
                    { label: 'Future improvements', detail: 'Topic analytics, saved revision plans, exam-mode flows, and adaptive difficulty progression.' },
                ],
            },
        ],
        relatedLinks: [
            { label: 'Back to My Works', url: '/myworks' },
        ],
    },
    'realtime-chat': {
        id: 'realtime-chat',
        name: 'Real-Time Chat Application',
        category: 'Realtime System',
        role: 'Full-Stack Developer',
        status: 'Live prototype',
        duration: 'Personal / portfolio build',
        summary: 'A room-based collaboration build using Socket.io and WebRTC to support messaging, audio, and video with low-latency behavior.',
        readingTime: '9 min read',
        heroNote: 'This case study is here to support both Work and System Design views, since it demonstrates persistent connections, signaling design, and realtime state handling.',
        keyMetrics: [
            { label: 'Latency', value: 'Sub-50ms messaging' },
            { label: 'Transport model', value: 'WebSocket + WebRTC' },
            { label: 'Scale lens', value: 'Room-based concurrency' },
        ],
        tags: ['Realtime', 'WebRTC', 'Socket.io', 'Architecture'],
        stack: ['React', 'Next.js', 'Node.js', 'Socket.io', 'WebRTC'],
        links: [
            { label: 'Live demo', url: 'https://whatsapp-mauve.vercel.app/' },
            { label: 'Source code', url: 'https://github.com/tuhinkairi/whatsapp-clone' },
        ],
        sections: [
            {
                id: 'executive-summary',
                title: 'Executive Summary',
                intro: 'This project is a technical showcase of why transport choices matter. The interface only works because the communication model matches the product need.',
                highlights: [
                    { label: 'Problem statement', detail: 'Request-response approaches break down for live room communication and media coordination.' },
                    { label: 'Approach', detail: 'Use persistent event transport for messaging and dedicated media transport for audio/video.' },
                    { label: 'Outcome', detail: 'A reusable collaboration architecture with sub-50ms message flow.' },
                ],
            },
            {
                id: 'system-design',
                title: 'System Design',
                intro: 'The design separates signaling from media so each layer can do one job well.',
                highlights: [
                    { label: 'Messaging layer', detail: 'Socket.io handles room presence, events, and instant message broadcast.' },
                    { label: 'Media layer', detail: 'WebRTC is used for peer media exchange instead of routing heavy streams through the app server.' },
                    { label: 'Frontend architecture', detail: 'Focused UI modules manage room state, message flow, and media behavior independently.' },
                ],
                mermaid: `flowchart LR
    A[User joins room] --> B[Socket connection]
    B --> C[Room events]
    C --> D[Message updates]
    C --> E[WebRTC signaling]
    E --> F[Peer media exchange]`,
            },
            {
                id: 'case-study',
                title: 'Case Study',
                intro: 'The case study value lies in showing how architecture, UX, and concurrency are tightly linked in realtime systems.',
                highlights: [
                    { label: 'Challenges', detail: 'Socket lifecycle management, room synchronization, and peer-connection reliability.' },
                    { label: 'Tradeoffs', detail: 'More architectural complexity than plain HTTP, but far better user experience for live collaboration.' },
                    { label: 'Lessons', detail: 'Clear event contracts and signaling boundaries prevent a lot of future instability.' },
                ],
            },
        ],
        relatedLinks: [
            { label: 'Back to My Works', url: '/myworks' },
            { label: 'Back to System Design', url: '/system-architecture-design' },
        ],
    },
    'fdrp-platform': {
        id: 'fdrp-platform',
        name: 'FDRP Journals Platform',
        category: 'Client Platform / Publishing',
        role: 'Full-Stack Developer',
        status: 'Production',
        duration: 'Client delivery',
        summary: 'A performance-focused publishing ecosystem covering journal brands, public publication surfaces, and editorial workflows with strong SEO and operational priorities.',
        readingTime: '12 min read',
        heroNote: 'This case study covers the platform-level engineering story behind the journal websites, while each client journal also has its own card and dedicated detail page.',
        keyMetrics: [
            { label: 'Lighthouse', value: '98+' },
            { label: 'SEO', value: '100' },
            { label: 'API latency', value: 'About 35% lower' },
        ],
        tags: ['Client work', 'SEO', 'Performance', 'Publishing'],
        stack: ['Next.js', 'React', 'Tailwind CSS', 'Python', 'Node.js', 'REST APIs'],
        links: [
            { label: 'Main platform', url: 'https://www.fdrpjournals.org/' },
            { label: 'Journal listing', url: 'https://www.fdrpjournals.org/journals' },
            { label: 'Editorial system', url: 'https://editorial.fdrpjournals.org/login' },
        ],
        sections: [
            {
                id: 'executive-summary',
                title: 'Executive Summary',
                intro: 'The platform challenge was bigger than building one site. It required creating a reliable publishing foundation that could support multiple journal brands without sacrificing speed or discoverability.',
                highlights: [
                    { label: 'Problem statement', detail: 'Academic publishing sites often become slow, operationally heavy, and difficult to evolve as content and journal count increase.' },
                    { label: 'Approach', detail: 'Build a modular platform with performance-conscious rendering, cleaner request flows, and maintainable journal-brand reuse.' },
                    { label: 'Outcome', detail: 'Strong SEO, better performance, and a scalable base for multiple journal properties.' },
                ],
            },
            {
                id: 'client-projects',
                title: 'Client Projects Under This Platform',
                intro: 'The following live properties were verified from the journal listing and are represented as individual client-project cards in the portfolio.',
                highlights: [
                    { label: 'INDJCMR', detail: 'https://www.indjcmr.com' },
                    { label: 'INDJCPR', detail: 'https://www.indjcpr.com' },
                    { label: 'INDJECE', detail: 'https://www.indjece.com' },
                    { label: 'INDJEEE', detail: 'https://www.indjeee.com' },
                    { label: 'INDJCST / IJSREAT / IJRTMR / The IJIRE', detail: 'https://indjcst.com, https://ijsreat.com, https://ijrtmr.com, https://theijire.com' },
                ],
            },
            {
                id: 'system-design',
                title: 'System Design and Architecture',
                intro: 'The platform succeeds because it treats performance and maintainability as first-class design constraints.',
                highlights: [
                    { label: 'Rendering model', detail: 'Next.js-driven public surfaces optimized for discoverability and content delivery.' },
                    { label: 'Platform reuse', detail: 'Shared patterns across journal brands reduce engineering overhead while preserving brand-specific presentation.' },
                    { label: 'Operational layer', detail: 'Editorial and admin flows integrate with the publishing system without overwhelming the public experience.' },
                ],
                mermaid: `flowchart TD
    A[Shared platform foundation] --> B[Journal-specific public sites]
    A --> C[Editorial workflows]
    A --> D[SEO and performance layer]
    C --> E[Admin and submission operations]`,
            },
            {
                id: 'case-study',
                title: 'Case Study',
                intro: 'This project demonstrates product thinking in a technical environment: the platform had to serve readers, authors, editors, and the business at the same time.',
                highlights: [
                    { label: 'Challenges', detail: 'Balancing public discoverability, repeated content workflows, performance, and brand extensibility.' },
                    { label: 'Solutions', detail: 'Dynamic imports, modular admin surfaces, and stricter request-path discipline.' },
                    { label: 'Impact', detail: 'Higher technical quality, improved internal workflows, and a more scalable publishing ecosystem.' },
                ],
            },
        ],
        relatedLinks: [
            { label: 'Back to My Works', url: '/myworks' },
            { label: 'Back to System Design', url: '/system-architecture-design' },
        ],
    },
    'ems-dashboard': {
        id: 'ems-dashboard',
        name: 'EMS Dashboard',
        category: 'Dashboard / Enterprise UI',
        role: 'Frontend Developer',
        status: 'Client system',
        duration: 'Client delivery',
        summary: 'A multi-role dashboard experience for monitoring, filtering, and managing operational workflows in one unified interface.',
        readingTime: '7 min read',
        heroNote: 'This case study focuses on operational UI, complex state coordination, and how internal products prioritize clarity and efficiency over surface-level visuals.',
        keyMetrics: [
            { label: 'Interface type', value: 'Operational dashboard' },
            { label: 'Focus', value: 'Workflow efficiency' },
            { label: 'Stack fit', value: 'React + Redux Toolkit' },
        ],
        tags: ['Dashboard', 'Enterprise UI', 'State management'],
        stack: ['React', 'Redux Toolkit', 'REST APIs', 'Visualization UI', 'Laravel'],
        links: [
            { label: 'Related login', url: 'https://editorial.fdrpjournals.org/login' },
        ],
        sections: [
            {
                id: 'executive-summary',
                title: 'Executive Summary',
                intro: 'The EMS Dashboard showcases a different engineering skill set: organizing operational complexity into a usable interface.',
                highlights: [
                    { label: 'Problem', detail: 'Teams need one place to monitor statuses, filter large data sets, and act quickly across role-specific workflows.' },
                    { label: 'Approach', detail: 'Build a stateful React dashboard with predictable UI patterns and API-backed views.' },
                    { label: 'Outcome', detail: 'A cleaner operational surface for repeat-heavy internal work.' },
                ],
            },
            {
                id: 'system-design',
                title: 'State, UI, and API Design',
                intro: 'Dashboard quality depends on clarity under density. That makes state coordination a central product concern.',
                highlights: [
                    { label: 'State management', detail: 'Redux Toolkit supports synchronized filters, alerts, and multi-panel data views.' },
                    { label: 'API design', detail: 'Data-driven widgets and operational actions depend on stable REST contracts.' },
                    { label: 'Frontend architecture', detail: 'Role-aware modules keep the interface composable as workflow needs expand.' },
                ],
            },
            {
                id: 'impact',
                title: 'Challenges, Value, and Lessons',
                intro: 'The hardest part is not rendering charts. It is making the interface actionable when many things are happening at once.',
                highlights: [
                    { label: 'Challenge', detail: 'Prevent data overload while still exposing enough context for decision-making.' },
                    { label: 'Business value', detail: 'Helps teams move faster through filtering, monitoring, and coordination tasks.' },
                    { label: 'Lesson learned', detail: 'Internal systems need strong information hierarchy every bit as much as customer-facing products.' },
                ],
            },
        ],
        relatedLinks: [
            { label: 'Back to My Works', url: '/myworks' },
        ],
    },
    'indjcmr': createClientProject({
        id: 'indjcmr',
        name: 'INDJCMR',
        url: 'https://www.indjcmr.com',
        audience: 'medical and clinical research readers and authors',
        specialty: 'journal delivery for medical research content and publication pathways',
        outcome: 'A live production journal site with stronger discoverability and consistent publishing structure.',
    }),
    'indjcpr': createClientProject({
        id: 'indjcpr',
        name: 'INDJCPR',
        url: 'https://www.indjcpr.com',
        audience: 'pharmaceutical research audiences',
        specialty: 'content delivery for pharmaceutical research publishing',
        outcome: 'A searchable journal presence aligned with the larger publishing platform.',
    }),
    'indjece': createClientProject({
        id: 'indjece',
        name: 'INDJECE',
        url: 'https://www.indjece.com',
        audience: 'electronics and communication engineering researchers',
        specialty: 'engineering-journal publishing with responsive public access',
        outcome: 'A branded, live engineering journal site built on a maintainable shared foundation.',
    }),
    'indjeee': createClientProject({
        id: 'indjeee',
        name: 'INDJEEE',
        url: 'https://www.indjeee.com',
        audience: 'electrical and electronics engineering researchers',
        specialty: 'engineering content discoverability and submission-friendly journal structure',
        outcome: 'A live journal experience tuned for clarity, consistency, and publishing credibility.',
    }),
    'indjcst': createClientProject({
        id: 'indjcst',
        name: 'INDJCST',
        url: 'https://indjcst.com',
        audience: 'computer science and technology researchers',
        specialty: 'technical research publishing with accessible content surfaces',
        outcome: 'A production-ready journal presence with reusable platform-level performance patterns.',
    }),
    'ijsreat': createClientProject({
        id: 'ijsreat',
        name: 'IJSREAT',
        url: 'https://ijsreat.com',
        audience: 'science, research, engineering, and technology audiences',
        specialty: 'broad multidisciplinary journal delivery',
        outcome: 'A live client property supported by a consistent publishing architecture.',
    }),
    'ijrtmr': createClientProject({
        id: 'ijrtmr',
        name: 'IJRTMR',
        url: 'https://ijrtmr.com',
        audience: 'research, technology, and management readers and contributors',
        specialty: 'multi-domain journal experience with strong information access',
        outcome: 'A discoverable journal surface integrated into the broader publishing ecosystem.',
    }),
    'the-ijire': createClientProject({
        id: 'the-ijire',
        name: 'The IJIRE',
        url: 'https://theijire.com',
        audience: 'innovation and research-focused contributors',
        specialty: 'research publishing and journal information delivery',
        outcome: 'A live production journal website with maintainable client-work patterns.',
    }),
}

const myWorksCards: CategoryCard[] = [
    {
        id: 'fdrp-platform',
        projectId: 'fdrp-platform',
        title: 'FDRP Journals Platform',
        description: 'Publishing platform engineering across performance, SEO, editorial workflows, and multi-site delivery.',
        category: 'Client platform',
        status: 'Production',
        role: 'Full-Stack Developer',
        duration: 'Client delivery',
        keyMetrics: ['98+ Lighthouse', 'SEO 100', '35% lower API latency'],
        tags: ['Publishing', 'SEO', 'Client work'],
        stack: ['Next.js', 'React', 'Python', 'Node.js'],
        detailSection: 'executive-summary',
        siteUrl: 'https://www.fdrpjournals.org/',
    },
    {
        id: 'medical-ai-bot',
        projectId: 'medical-ai-bot',
        title: 'Medical AI Bot',
        description: 'Healthcare-oriented AI assistant concept built around symptom intake, triage-style reasoning, and safe scope control.',
        category: 'AI product',
        status: 'Proof of concept',
        role: 'Full-Stack Engineer',
        duration: 'Prototype',
        keyMetrics: ['Healthcare support workflow', 'NLP-driven reasoning', 'Decision-support framing'],
        tags: ['Healthcare', 'AI', 'NLP'],
        stack: ['Python', 'Flask', 'ML', 'Frontend UI'],
        detailSection: 'executive-summary',
    },
    {
        id: 'web-explainer',
        projectId: 'web-explainer',
        title: 'Web Explainer',
        description: 'AI workflow for turning dense webpages into streamed, readable explanations with parsing and prompt orchestration.',
        category: 'AI product',
        status: ' prototype',
        role: 'Full-Stack / AI Integration Engineer',
        duration: 'Personal product',
        keyMetrics: ['Fetch -> parse -> explain', 'Streaming UX', 'LLM pipeline'],
        tags: ['LLM', 'Streaming', 'Parsing'],
        stack: ['HTML', 'JavaScript', 'LLM integration'],
        detailSection: 'executive-summary',
        sourceUrl: 'https://github.com/tuhinkairi/Web-Explainer',
        siteUrl: 'https://web-explainer.vercel.app/',
    },
    {
        id: 'onepointprep',
        projectId: 'onepointprep',
        title: 'OnePointPrep',
        description: 'Student-focused AI learning workflow designed for concise prep journeys instead of broad educational overload.',
        category: 'Education AI',
        status: 'Backend',
        role: 'Full-Stack / AI Product Builder',
        duration: 'Personal build',
        keyMetrics: ['Student prep focus', 'Learning-flow design', 'Structured revision support'],
        tags: ['Education', 'AI', 'Product thinking'],
        stack: ['AI workflow', 'Learning UX', 'Study support'],
        detailSection: 'executive-summary',
        sourceUrl: 'https://github.com/tuhinkairi/OnePointPrep',
    },
    {
        id: 'e-store',
        projectId: 'e-store',
        title: 'E-store',
        description: 'Commerce-oriented storefront project that adds product discovery, catalog structure, and reusable shopping UI patterns.',
        category: 'Commerce',
        status: 'Backend',
        role: 'Frontend / Full-Stack Developer',
        duration: 'Personal build',
        keyMetrics: ['Storefront UI', 'Catalog architecture', 'Reusable card patterns'],
        tags: ['Commerce', 'Catalog', 'UI architecture'],
        stack: ['Web UI', 'Catalog flows', 'Components'],
        detailSection: 'executive-summary',
        sourceUrl: 'https://github.com/tuhinkairi/E-store',
    },
    {
        id: 'realtime-chat',
        projectId: 'realtime-chat',
        title: 'Real-Time Chat Application',
        description: 'Room-based messaging and media system built around WebSocket events and WebRTC signaling.',
        category: 'Realtime system',
        status: 'Live prototype',
        role: 'Full-Stack Developer',
        duration: 'Personal build',
        keyMetrics: ['Sub-50ms messaging', 'WebRTC media', 'Room concurrency'],
        tags: ['Realtime', 'WebRTC', 'Sockets'],
        stack: ['React', 'Node.js', 'Socket.io', 'WebRTC'],
        detailSection: 'executive-summary',
        siteUrl: 'https://whatsapp-mauve.vercel.app/',
        sourceUrl: 'https://github.com/tuhinkairi/whatsapp-clone',
    },
    {
        id: 'ems-dashboard',
        projectId: 'ems-dashboard',
        title: 'EMS Dashboard',
        description: 'Operational dashboard for alerting, filtering, monitoring, and multi-role workflow coordination.',
        category: 'Enterprise UI',
        status: 'Client system',
        role: 'Frontend Developer',
        duration: 'Client delivery',
        keyMetrics: ['Operational visibility', 'Role-aware flows', 'State-heavy UI'],
        tags: ['Dashboard', 'Enterprise', 'Redux'],
        stack: ['React', 'Redux Toolkit', 'REST APIs'],
        detailSection: 'executive-summary',
        siteUrl: 'https://editorial.fdrpjournals.org/login',
    },
    {
        id: 'indjcmr',
        projectId: 'indjcmr',
        title: 'INDJCMR',
        description: 'Client journal website for medical research publishing, public discovery, and author-facing clarity.',
        category: 'Client project',
        status: 'production',
        role: 'Full-Stack Developer',
        duration: 'Client delivery',
        keyMetrics: ['Live journal site', 'SEO-led delivery', 'Responsive publishing UX'],
        tags: ['Client work', 'Medical research', 'Publishing'],
        stack: clientBaseStack,
        detailSection: 'executive-summary',
        siteUrl: 'https://www.indjcmr.com',
    },
    {
        id: 'indjcpr',
        projectId: 'indjcpr',
        title: 'INDJCPR',
        description: 'Pharmaceutical research journal website delivered as part of the FDRP publishing ecosystem.',
        category: 'Client project',
        status: 'production',
        role: 'Full-Stack Developer',
        duration: 'Client delivery',
        keyMetrics: ['Live journal site', 'Shared platform architecture', 'Discoverability focus'],
        tags: ['Client work', 'Pharma research', 'Publishing'],
        stack: clientBaseStack,
        detailSection: 'executive-summary',
        siteUrl: 'https://www.indjcpr.com',
    },
    {
        id: 'indjece',
        projectId: 'indjece',
        title: 'INDJECE',
        description: 'Engineering journal website for electronics and communication publishing workflows.',
        category: 'Client project',
        status: 'production',
        role: 'Full-Stack Developer',
        duration: 'Client delivery',
        keyMetrics: ['Live journal site', 'Engineering audience', 'Responsive delivery'],
        tags: ['Client work', 'Engineering', 'Publishing'],
        stack: clientBaseStack,
        detailSection: 'executive-summary',
        siteUrl: 'https://www.indjece.com',
    },
    {
        id: 'indjeee',
        projectId: 'indjeee',
        title: 'INDJEEE',
        description: 'Electrical and electronics engineering journal website delivered with shared publishing-system quality.',
        category: 'Client project',
        status: 'production',
        role: 'Full-Stack Developer',
        duration: 'Client delivery',
        keyMetrics: ['Live journal site', 'Engineering publishing', 'SEO-first structure'],
        tags: ['Client work', 'EEE', 'Publishing'],
        stack: clientBaseStack,
        detailSection: 'executive-summary',
        siteUrl: 'https://www.indjeee.com',
    },
    {
        id: 'indjcst',
        projectId: 'indjcst',
        title: 'INDJCST',
        description: 'Computer science and technology journal website built on the shared FDRP platform.',
        category: 'Client project',
        status: 'production',
        role: 'Full-Stack Developer',
        duration: 'Client delivery',
        keyMetrics: ['Live journal site', 'Technical audience', 'Shared architecture'],
        tags: ['Client work', 'CS', 'Publishing'],
        stack: clientBaseStack,
        detailSection: 'executive-summary',
        siteUrl: 'https://indjcst.com',
    },
    {
        id: 'ijsreat',
        projectId: 'ijsreat',
        title: 'IJSREAT',
        description: 'Multidisciplinary journal website optimized for public publication access and reusable delivery quality.',
        category: 'Client project',
        status: 'production',
        role: 'Full-Stack Developer',
        duration: 'Client delivery',
        keyMetrics: ['Live journal site', 'Multidisciplinary scope', 'Performance-aware build'],
        tags: ['Client work', 'Research', 'Publishing'],
        stack: clientBaseStack,
        detailSection: 'executive-summary',
        siteUrl: 'https://ijsreat.com',
    },
    {
        id: 'ijrtmr',
        projectId: 'ijrtmr',
        title: 'IJRTMR',
        description: 'Research, technology, and management journal website delivered as individual client work.',
        category: 'Client project',
        status: 'production',
        role: 'Full-Stack Developer',
        duration: 'Client delivery',
        keyMetrics: ['Live journal site', 'Management + tech', 'Platform-aligned UX'],
        tags: ['Client work', 'Management', 'Publishing'],
        stack: clientBaseStack,
        detailSection: 'executive-summary',
        siteUrl: 'https://ijrtmr.com',
    },
    {
        id: 'the-ijire',
        projectId: 'the-ijire',
        title: 'The IJIRE',
        description: 'Innovation and research journal website delivered with consistent publishing UX and maintainable structure.',
        category: 'Client project',
        status: 'production',
        role: 'Full-Stack Developer',
        duration: 'Client delivery',
        keyMetrics: ['Live journal site', 'Innovation research', 'Client delivery'],
        tags: ['Client work', 'Innovation', 'Publishing'],
        stack: clientBaseStack,
        detailSection: 'executive-summary',
        siteUrl: 'https://theijire.com',
    },
]

export const CATEGORY_PAGES: Record<string, CategoryPage> = {
    myworks: {
        eyebrow: 'Selected Work',
        title: 'A concise view of the projects that best represent product delivery and engineering range.',
        intro: 'Each card highlights the problem space, role, stack, and visible outcomes, with the full technical story available inside a dedicated project page.',
        purpose: 'Use this page for a quick understanding of the work, then open the projects that match the domain, system type, or engineering depth you want to review.',
        cards: myWorksCards,
    },
    'case-studies': {
        eyebrow: 'Case Studies',
        title: 'Case studies centered on problem-solving, implementation choices, and measurable impact.',
        intro: 'This page brings together projects with strong engineering narratives, showing how requirements were translated into architecture, product decisions, and delivered results.',
        purpose: 'Use this section when you want to understand how the work was approached, what tradeoffs were made, and what value the final solution created.',
        cards: [
            { ...myWorksCards.find((card) => card.projectId === 'web-explainer')!, detailSection: 'engineering-challenges' },
            myWorksCards.find((card) => card.projectId === 'medical-ai-bot')!,
            { ...myWorksCards.find((card) => card.projectId === 'onepointprep')!, detailSection: 'future-roadmap' },
            { ...myWorksCards.find((card) => card.projectId === 'realtime-chat')!, detailSection: 'case-study' },
            { ...myWorksCards.find((card) => card.projectId === 'fdrp-platform')!, detailSection: 'case-study' },
            { ...myWorksCards.find((card) => card.projectId === 'ems-dashboard')!, detailSection: 'impact' },
        ],
    },
    'system-architecture-design': {
        eyebrow: 'System Design',
        title: 'Systems work that highlights architecture decisions, data flow, scalability, and technical tradeoffs.',
        intro: 'These projects are selected for the strength of their underlying design, from service boundaries and state handling to realtime communication and performance strategy.',
        purpose: 'Use this page when you want to evaluate system thinking first and then move into the full project context only where needed.',
        cards: [
            { ...myWorksCards.find((card) => card.projectId === 'web-explainer')!, detailSection: 'system-design' },
            { ...myWorksCards.find((card) => card.projectId === 'medical-ai-bot')!, detailSection: 'system-design' },
            { ...myWorksCards.find((card) => card.projectId === 'onepointprep')!, detailSection: 'system-design' },
            { ...myWorksCards.find((card) => card.projectId === 'realtime-chat')!, detailSection: 'system-design' },
            { ...myWorksCards.find((card) => card.projectId === 'fdrp-platform')!, detailSection: 'system-design' },
            { ...myWorksCards.find((card) => card.projectId === 'ems-dashboard')!, detailSection: 'system-design' },
        ],
    },
}

export { caseStudyHref }
